import Image from "next/image";
import { useEffect, useState } from "react";

import { PdfIcon } from "@/assets/icons/ui/Pdf";
import EmptyState from "@/assets/images/home/EmptyState2.png";
import type { CommonModels } from "@/data/common/common.models";
import { PdfUtils } from "@/util/pdf.utils";

interface IProps {
  project?: CommonModels.ProjectWithMediaDto;
  isCoverImage?: boolean;
  placeholderImage?: string;
}

const ProfileProjectMediaItem = ({ project, isCoverImage = false, placeholderImage }: IProps) => {
  const pdfMedia = project?.media?.find((media) => media.mimeType === "application/pdf");
  const imageMedia = project?.media?.find((media) => media.mimeType.includes("image/"));

  const [generatedThumbnail, setGeneratedThumbnail] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (!pdfMedia?.url || generatedThumbnail || isGenerating) {
      return;
    }

    setIsGenerating(true);

    PdfUtils.generatePdfThumbnail(pdfMedia.url)
      .then((thumbnail) => {
        setGeneratedThumbnail(thumbnail);
      })
      .finally(() => {
        setIsGenerating(false);
      });
  }, [pdfMedia?.url, generatedThumbnail, isGenerating]);

  if (!imageMedia && pdfMedia) {
    if (pdfMedia?.thumbnailUrl) {
      return (
        <Image
          src={pdfMedia?.thumbnailUrl || EmptyState.src}
          alt="Project image"
          className="mx-auto w-fit object-cover outline-hidden!"
          width={0}
          height={0}
          sizes="(min-width: 300px) 300px, 300px"
          data-testid="project-image"
        />
      );
    }

    if (!generatedThumbnail) {
      return (
        <PdfIcon
          className="h-12 w-12"
          data-testid="project-image"
        />
      );
    }

    return (
      <Image
        src={generatedThumbnail}
        alt="Project image"
        className="mx-auto w-fit object-cover outline-hidden!"
        width={0}
        height={0}
        sizes="(min-width: 300px) 300px, 300px"
        data-testid="project-image"
      />
    );
  }
  if (isCoverImage || imageMedia?.thumbnailUrl || imageMedia?.url) {
    return (
      <Image
        src={imageMedia?.url || imageMedia?.thumbnailUrl || placeholderImage || EmptyState.src}
        alt="Project image"
        className="mx-auto w-fit object-cover outline-hidden!"
        fill
        sizes="(min-width: 500px) 500px, 500px"
        data-testid="project-image"
      />
    );
  }
  if (placeholderImage) {
    return (
      <Image
        src={placeholderImage}
        alt="Project image"
        className="object-cover outline-hidden!"
        fill
        data-testid="project-image"
      />
    );
  }
  return null;
};

export default ProfileProjectMediaItem;
